import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Repositories API
export const getRepositories = () => api.get('/repos', { timeout: 20000 });
export const syncRepositories = () => api.post('/repos/sync');
export const createRepository = (data) => api.post('/repos', data);
export const browseRepositories = () => api.get('/repos/browse', { timeout: 20000 });
export const getRepository = (owner, repo) => api.get(`/repos/${owner}/${repo}`, { timeout: 20000 });
export const getMyAccess = () => api.get('/repos/my-access', { timeout: 20000 });

// Access Management API
export const grantAccess = (repoId, username, permission, expiresAt) =>
  api.post('/access/grant', { repoId, username, permission, expiresAt });
export const revokeAccess = (repoId, username) => api.post('/access/revoke', { repoId, username });
export const createAccessRequest = (repoId, requestedFor, permission, reason, serviceDeskRequestNumber) =>
  api.post('/access/request', { repoId, requestedFor, permission, reason, serviceDeskRequestNumber });
export const createAccessRequestByName = (fullName, permission, reason, requestedFor, serviceDeskRequestNumber) =>
  api.post('/access/request-by-name', { fullName, permission, reason, requestedFor, serviceDeskRequestNumber });
export const getAccessRequests = () => api.get('/access/requests');
export const reviewAccessRequest = (requestId, action, permission, expiresAt) =>
  api.post(`/access/requests/${requestId}/review`, { action, permission, expiresAt });

// Admin API
export const getAllUsers = () => api.get('/admin/users');
export const getUserRepositories = (userId) => api.get(`/admin/users/${userId}/repositories`);
export const grantUserRepoAccess = (userId, repoId, permission) =>
  api.post(`/admin/users/${userId}/grant-repo-access`, { repoId, permission });
export const revokeUserRepoAccess = (userId, repoId) =>
  api.post(`/admin/users/${userId}/revoke-repo-access`, { repoId });
export const setUserGitHubUsername = (userId, githubUsername) =>
  api.post(`/admin/users/${userId}/set-github-username`, { githubUsername });
export const inviteUserToGitHubOrg = (userId) =>
  api.post(`/admin/users/${userId}/invite-github-org`);
export const syncGitHubCollaborators = () => api.post('/admin/sync-github-collaborators');
export const bulkGrantRepoAccess = (userIds, repoId, permission, expiresAt) =>
  api.post('/admin/users/bulk-grant-repo-access', { userIds, repoId, permission, expiresAt });
export const getAdminStatistics = (period = '30') =>
  api.get('/admin/statistics', { params: { period } });
export const getActivityLogs = (params) => api.get('/admin/activity-logs', { params });
export const exportActivityLogs = (params) =>
  api.get('/admin/activity-logs/export', { params, responseType: 'blob' });
export const getAccessMatrix = () => api.get('/admin/access-matrix');
export const getRecentActivity = (limit = 10) =>
  api.get('/admin/recent-activity', { params: { limit } });
export const updateUserNotes = (userId, notes) =>
  api.put(`/admin/users/${userId}/notes`, { notes });
export const updateUserTags = (userId, tags) =>
  api.put(`/admin/users/${userId}/tags`, { tags });
export const setPasswordExpiry = (userId, expiryDate) =>
  api.put(`/admin/users/${userId}/password-expiry`, { expiryDate });
export const sendBulkEmail = (userIds, subject, message) =>
  api.post('/admin/bulk-email', { userIds, subject, message });

// Notifications API
export const getNotifications = (page = 1, limit = 20) => api.get('/notifications', { params: { page, limit } });
export const getUnreadNotificationCount = () => api.get('/notifications/unread-count');
export const markNotificationRead = (id) => api.post(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.post('/notifications/read-all');

// GitHub App & SSO
export const getGitHubAppStatus = () => api.get('/github-app/status');
export const getGitHubAppInstallUrl = () => api.get('/github-app/install');
export const getSsoStatus = () => api.get('/auth/sso/status');

// Repository settings
export const updateRepoSettings = (repoId, settings) =>
  api.patch(`/repos/settings/${repoId}`, settings);

// Access review campaigns
export const getReviewCampaigns = () => api.get('/reviews/campaigns');
export const getReviewCampaign = (id) => api.get(`/reviews/campaigns/${id}`);
export const createReviewCampaign = (data) => api.post('/reviews/campaigns', data);
export const activateReviewCampaign = (id) => api.post(`/reviews/campaigns/${id}/activate`);
export const completeReviewCampaign = (id) => api.post(`/reviews/campaigns/${id}/complete`);
export const getMyReviewItems = () => api.get('/reviews/my-items');
export const decideReviewItem = (campaignId, itemIndex, decision, notes) =>
  api.post(`/reviews/campaigns/${campaignId}/items/${itemIndex}/decide`, { decision, notes });

// Enterprise / governance
export const getAccessPolicies = () => api.get('/enterprise/policies');
export const createAccessPolicy = (data) => api.post('/enterprise/policies', data);
export const updateAccessPolicy = (id, data) => api.put(`/enterprise/policies/${id}`, data);
export const deleteAccessPolicy = (id) => api.delete(`/enterprise/policies/${id}`);
export const evaluateAccessPolicy = (repoId, permission, reason) =>
  api.post('/enterprise/policies/evaluate', { repoId, permission, reason });

export const getBreakGlassRequests = () => api.get('/enterprise/break-glass');
export const createBreakGlass = (data) => api.post('/enterprise/break-glass', data);
export const revokeBreakGlass = (id) => api.post(`/enterprise/break-glass/${id}/revoke`);
export const auditBreakGlass = (id, notes) => api.post(`/enterprise/break-glass/${id}/audit`, { notes });

export const getTeamAccess = () => api.get('/enterprise/team-access');
export const grantTeamAccess = (data) => api.post('/enterprise/team-access', data);
export const revokeTeamAccess = (id) => api.post(`/enterprise/team-access/${id}/revoke`);

export const setRepoDelegates = (repoId, userIds) =>
  api.put(`/enterprise/repos/${repoId}/delegates`, { userIds });

export const getFavorites = () => api.get('/enterprise/favorites');
export const toggleFavorite = (repoId) => api.post(`/enterprise/favorites/${repoId}`);

export const getMyTemporaryAccess = () => api.get('/enterprise/temporary-access/mine');
export const getMyExpiredAccess = () => api.get('/enterprise/temporary-access/expired');
export const requestAccessRenewal = (id, reason, extendDays) =>
  api.post(`/enterprise/temporary-access/${id}/renew`, { reason, extendDays });

export const getEnterpriseAnalytics = (days = 30) =>
  api.get('/enterprise/analytics/overview', { params: { days } });
export const getBackgroundJobs = () => api.get('/enterprise/jobs');
export const runAccessExpiryJob = () => api.post('/enterprise/jobs/access_expiry/run');
export const getTokenHealth = () => api.get('/enterprise/token-health');
export const getOrganization = () => api.get('/enterprise/organization');
export const updateOrganization = (data) => api.put('/enterprise/organization', data);
export const exportAuditToSiem = (hours = 24) =>
  api.post('/enterprise/audit/export-siem', { hours });

export const setMyGitHubUsername = (githubUsername) =>
  api.post('/auth/github-username', { githubUsername });

export default api;
